import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Typography,
  Stack,
  Button,
  Drawer,
  Box,
  Rating,
  TextField,
  IconButton,
} from '@mui/material';
import { RateReview } from '@mui/icons-material';

import { DirigoShortLogo } from '@shared/components/Logo/DirigoShortLogo';

import { FeedbackContext } from '@cvt/contexts';
import { useStepEntryCrud } from '../hooks/useStepEntryCrud';
import { BackButton } from '../components/BackButton';
import { Divider } from '../components/Divider';
import { TopActions } from '../components/TopActions';


interface Props {
  enrolmentId: number
  program: Programs.Program;
  lastStepEntry?: Programs.StepEntry;
  onViewJourney: () => void;
}

export const ProgramCompletion: FC<Props> = ({ enrolmentId, program, lastStepEntry, onViewJourney }) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { triggerFeedback } = React.useContext(FeedbackContext);

  const { updateStepEntry } = useStepEntryCrud();
  const navigate = useNavigate();

  const handleSubmitFeedback = async () => {
    if (!lastStepEntry) return;
    try {
      await updateStepEntry.mutateAsync({
        id: lastStepEntry.id,
        rating,
        feedbackComment,
      });

      triggerFeedback({
        severity: 'success',
        message: 'Feedback successfully submitted',
      });

      setSubmitted(true);
      setFeedbackOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box position="relative">
      <TopActions>
        <BackButton onClick={() => navigate(`/programs/journey/${enrolmentId}/today`, { replace: true })}/>
      </TopActions>
      <Stack alignItems="center" justifyContent="center" spacing={4} sx={{ minHeight: '80vh' }} px={4}>
        <DirigoShortLogo height={48} sx={{ opacity: 0.4 }}/>
        <Typography variant="h3" textAlign="center" fontSize={24}>
          {program.completionTitle || 'Well done!'}
        </Typography>
        {program.completionSubtitle && (
          <Typography variant="overline" textAlign="center" color="text.secondary" maxWidth={500}>
            {program.completionSubtitle}
          </Typography>
        )}
        <Divider/>
        <Button variant="outlined" size="large" onClick={onViewJourney}>
          View My Journey
        </Button>
      </Stack>

      {!submitted && (
        <IconButton
          onClick={() => setFeedbackOpen(true)}
          sx={{
            position: 'fixed',
            top: '25%',
            right: 0,
            transform: 'translateY(-50%)',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '8px 0 0 8px',
            px: 0.5,
            py: 2,
            '&:hover': { backgroundColor: 'primary.dark' },
          }}
        >
          <RateReview fontSize="small"/>
        </IconButton>
      )}

      <Drawer
        anchor="right"
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      >
        <Box sx={{ width: 320, p: 3 }}>
          <Stack spacing={3}>
            <Typography variant="h6">
              How was this activation?
            </Typography>
            <Stack alignItems="center">
              <Rating
                size="large"
                value={rating}
                onChange={(_, value) => setRating(value)}
              />
            </Stack>
            <TextField
              multiline
              minRows={3}
              maxRows={6}
              placeholder="Any comments or observations?"
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleSubmitFeedback}
              disabled={!rating}
            >
              Submit Feedback
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
};
